import { useMetaTemplates, type WhatsAppTemplate } from '@/hooks/useMetaTemplates'
import { InboxTemplatePreview } from './InboxTemplatePreview'
import { ClipboardList } from 'lucide-react'

interface TemplateMessagePreviewProps {
  templateName: string
  language?: string
  variables?: Record<string, any>
  businessAccountId?: string
  headerMediaUrl?: string // Uploaded media URL to override template default
  carouselMediaUrls?: Record<string, string> // Card index -> uploaded card media
}

export function TemplateMessagePreview({
  templateName,
  language,
  variables,
  businessAccountId,
  headerMediaUrl,
  carouselMediaUrls
}: TemplateMessagePreviewProps) {
  const { data: templatesData, isLoading } = useMetaTemplates(businessAccountId)

  const versions = templatesData?.templates?.filter((t: WhatsAppTemplate) => t.name === templateName) || []
  const fullTemplate = versions.find((t: WhatsAppTemplate) => t.language === language) ||
    ((!language || versions.length === 1) ? versions[0] : undefined)

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <span>📋</span>
        <span>Loading template...</span>
      </div>
    )
  }

  if (fullTemplate) {
    let processedComponents = fullTemplate.components || []
    if (fullTemplate.category?.toUpperCase() === 'AUTHENTICATION') {
      processedComponents = processedComponents.map((comp: any) => {
        if (comp.type?.toUpperCase() !== 'BODY' || comp.text) return comp
        return {
          ...comp,
          text: `{{1}} is your verification code.${comp.add_security_recommendation ? '\n\nFor your security, do not share this code.' : ''}`,
        }
      })
    }

    if (headerMediaUrl) {
      processedComponents = processedComponents.map((comp: any) => {
        if ((comp.type === 'HEADER' || comp.type === 'header') && 
            (comp.format === 'IMAGE' || comp.format === 'VIDEO' || comp.format === 'DOCUMENT')) {
          return {
            ...comp,
            mediaUrl: headerMediaUrl, // Override with uploaded media URL
            example: {
              ...comp.example,
              header_handle: undefined,
              header_url: [headerMediaUrl] // Use uploaded URL
            }
          }
        }
        return comp
      })
    }

    // The campaign's own card media, not the template's sample images.
    if (carouselMediaUrls && Object.keys(carouselMediaUrls).length > 0) {
      processedComponents = processedComponents.map((comp: any) => {
        if (String(comp?.type || '').toUpperCase() !== 'CAROUSEL' || !Array.isArray(comp.cards)) return comp
        return {
          ...comp,
          cards: comp.cards.map((card: any, idx: number) => {
            const url = carouselMediaUrls[String(idx)]
            if (!url || !Array.isArray(card?.components)) return card
            return {
              ...card,
              components: card.components.map((cc: any) =>
                String(cc?.type || '').toUpperCase() === 'HEADER' ? { ...cc, mediaUrl: url } : cc
              ),
            }
          }),
        }
      })
    }

    if (variables && Object.keys(variables).length > 0 && processedComponents.length > 0) {
      const replaceVariablesInText = (text: string, vars: Record<string, any>): string => {
        let processedText = text
        
        const variableKeys = Object.keys(vars)
        const isPositional = variableKeys.every(key => /^\d+$/.test(key))
        
        if (isPositional) {
          const variableValues = Object.keys(vars)
            .sort((a, b) => parseInt(a) - parseInt(b))
            .map(key => vars[key])
          
          variableValues.forEach((value, idx) => {
            const varPattern = new RegExp(`\\{\\{${idx + 1}\\}\\}`, 'g')
            processedText = processedText.replace(varPattern, String(value))
          })
        } else {
          Object.entries(vars).forEach(([key, value]) => {
            const varPattern = new RegExp(`\\{\\{${key}\\}\\}`, 'g')
            processedText = processedText.replace(varPattern, String(value))
          })
        }
        
        return processedText
      }
      
      processedComponents = processedComponents.map((comp: any) => {
        if ((comp.type === 'BODY' || comp.type === 'body') && comp.text) {
          return {
            ...comp,
            text: replaceVariablesInText(comp.text, variables)
          }
        }

        if ((comp.type === 'HEADER' || comp.type === 'header') &&
            comp.format === 'TEXT' && comp.text) {
          return {
            ...comp,
            text: replaceVariablesInText(comp.text, variables)
          }
        }

        if ((comp.type === 'BUTTONS' || comp.type === 'buttons') && Array.isArray(comp.buttons)) {
          return {
            ...comp,
            buttons: comp.buttons.map((btn: any) => {
              if (btn.url && /\{\{\d+\}\}/.test(btn.url)) {
                return { ...btn, url: replaceVariablesInText(btn.url, variables) }
              }
              return btn
            })
          }
        }

        return comp
      })
    }

    const templateForPreview = {
      ...fullTemplate,
      components: processedComponents
    }

    return (
      <div className="max-w-sm -mx-2">
        <InboxTemplatePreview
          template={templateForPreview as any}
          className="bg-transparent"
        />
      </div>
    )
  }

  return (
    <div
      className="max-w-xs"
      style={{ fontFamily: 'var(--wn-font)' }}
    >
      <div className="flex items-center gap-2.5 pb-2.5 border-b border-[#edf1f7]">
        <div
          className="h-9 w-9 rounded-[10px] flex items-center justify-center shrink-0"
          style={{ background: '#E8F8F0' }}
        >
          <ClipboardList className="h-4 w-4" style={{ color: '#1EB677' }} />
        </div>
        <div className="flex-1 min-w-0">
          <div
            className="truncate"
            style={{ fontFamily: 'var(--wn-font)', fontSize: 13, fontWeight: 700, color: '#101828' }}
          >
            Template Message
          </div>
          <div
            className="truncate mt-0.5"
            style={{ fontFamily: 'var(--wn-font)', fontSize: 11, fontWeight: 500, color: '#6a7282' }}
          >
            {templateName}
          </div>
        </div>
      </div>
      {variables && Object.keys(variables).length > 0 && (
        <div className="mt-2.5 space-y-1.5">
          {Object.entries(variables).map(([key, value], idx) => (
            <div
              key={idx}
              className="flex items-baseline gap-1.5"
              style={{ fontFamily: 'var(--wn-font)', fontSize: 12 }}
            >
              <span style={{ color: '#6a7282', fontWeight: 600 }}>{key}:</span>
              <span style={{ color: '#101828', fontWeight: 500, overflowWrap: 'anywhere' }}>
                {String(value)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
